import { useMemo, useRef, useState, useCallback } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const TYPE_COLORS = {
  core: { fill: "#10B981", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.5)", text: "#D1FAE5" },
  detail: { fill: "#6366F1", bg: "rgba(99,102,241,0.12)", border: "rgba(99,102,241,0.5)", text: "#E0E7FF" },
  formula: { fill: "#F59E0B", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.5)", text: "#FEF3C7" },
};

const NODE_W = 168;
const NODE_H = 52;
const X_GAP = 240;
const Y_GAP = 74;

function computeLayout(root, collapsed) {
  const positions = {};
  const edges = [];
  let leaf = 0;

  function walk(node, depth) {
    const x = depth * X_GAP + 30;
    const isCollapsed = collapsed.has(node.id);
    const children = !isCollapsed && Array.isArray(node.children) ? node.children : [];
    let y;
    if (children.length === 0) {
      y = leaf * Y_GAP + 40;
      leaf += 1;
    } else {
      const ys = children.map((c) => {
        edges.push({ from: node.id, to: c.id });
        return walk(c, depth + 1);
      });
      y = (ys[0] + ys[ys.length - 1]) / 2;
    }
    positions[node.id] = { x, y, node, depth, hasChildren: Array.isArray(node.children) && node.children.length > 0 };
    return y;
  }
  if (root) walk(root, 0);
  return { positions, edges };
}

export default function Mindmap({ data }) {
  const [collapsed, setCollapsed] = useState(new Set());
  const [transform, setTransform] = useState({ x: 20, y: 20, scale: 1 });
  const dragRef = useRef(null);

  const { positions, edges } = useMemo(() => computeLayout(data, collapsed), [data, collapsed]);

  const nodes = Object.values(positions);
  const maxX = Math.max(200, ...nodes.map((n) => n.x)) + NODE_W + 40;
  const maxY = Math.max(120, ...nodes.map((n) => n.y)) + NODE_H + 40;

  const toggle = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onMouseDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, ox: transform.x, oy: transform.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setTransform((t) => ({ ...t, x: dragRef.current.ox + dx, y: dragRef.current.oy + dy }));
  };
  const onMouseUp = () => { dragRef.current = null; };
  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setTransform((t) => ({ ...t, scale: Math.min(2.2, Math.max(0.4, t.scale * delta)) }));
  };

  const zoom = (dir) => setTransform((t) => ({ ...t, scale: Math.min(2.2, Math.max(0.4, t.scale + dir * 0.15)) }));
  const reset = () => setTransform({ x: 20, y: 20, scale: 1 });

  if (!data || !data.label) {
    return <div className="text-slate-500 text-sm p-6">No mindmap available.</div>;
  }

  return (
    <div className="relative rounded-2xl border border-[#1E293B] bg-[#090D16] overflow-hidden" data-testid="mindmap-container">
      <div className="absolute top-3 right-3 z-10 flex gap-2">
        <button data-testid="mindmap-zoom-in" onClick={() => zoom(1)} className="w-9 h-9 rounded-lg bg-[#111726] border border-[#1E293B] flex items-center justify-center text-slate-300 hover:border-emerald-500/50 transition-colors">
          <ZoomIn className="w-4 h-4" />
        </button>
        <button data-testid="mindmap-zoom-out" onClick={() => zoom(-1)} className="w-9 h-9 rounded-lg bg-[#111726] border border-[#1E293B] flex items-center justify-center text-slate-300 hover:border-emerald-500/50 transition-colors">
          <ZoomOut className="w-4 h-4" />
        </button>
        <button data-testid="mindmap-reset" onClick={reset} className="w-9 h-9 rounded-lg bg-[#111726] border border-[#1E293B] flex items-center justify-center text-slate-300 hover:border-emerald-500/50 transition-colors">
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      <div className="absolute bottom-3 left-3 z-10 flex gap-3 text-xs">
        {Object.entries(TYPE_COLORS).map(([k, c]) => (
          <div key={k} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.fill }} />
            <span className="text-slate-400 capitalize">{k}</span>
          </div>
        ))}
      </div>

      <div
        className="w-full h-[460px] cursor-grab active:cursor-grabbing"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onWheel={onWheel}
      >
        <svg width="100%" height="100%">
          <g transform={`translate(${transform.x}, ${transform.y}) scale(${transform.scale})`}>
            {edges.map((e, i) => {
              const from = positions[e.from];
              const to = positions[e.to];
              if (!from || !to) return null;
              const x1 = from.x + NODE_W;
              const y1 = from.y + NODE_H / 2;
              const x2 = to.x;
              const y2 = to.y + NODE_H / 2;
              const mx = (x1 + x2) / 2;
              return (
                <path
                  key={i}
                  d={`M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`}
                  stroke="#1E293B"
                  strokeWidth="1.5"
                  fill="none"
                />
              );
            })}
            {nodes.map(({ x, y, node, hasChildren }) => {
              const c = TYPE_COLORS[node.type] || TYPE_COLORS.detail;
              const isCollapsed = collapsed.has(node.id);
              return (
                <g key={node.id} data-testid="mindmap-node" onClick={() => hasChildren && toggle(node.id)} style={{ cursor: hasChildren ? "pointer" : "default" }}>
                  <foreignObject x={x} y={y} width={NODE_W} height={NODE_H}>
                    <div
                      style={{
                        width: NODE_W - 4, height: NODE_H, display: "flex", alignItems: "center", gap: 8,
                        padding: "0 12px", borderRadius: 12, background: c.bg, border: `1px solid ${c.border}`,
                        color: c.text, fontSize: 12.5, fontWeight: 600, lineHeight: 1.2, boxSizing: "border-box",
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: c.fill, flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {node.label}
                      </span>
                      {hasChildren && (
                        <span style={{ marginLeft: "auto", fontSize: 10, color: c.fill }}>{isCollapsed ? "+" : "−"}</span>
                      )}
                    </div>
                  </foreignObject>
                </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
}
